import React from 'react';
import { ChevronRight, Sparkles, Zap } from 'lucide-react';
import { SUBSCRIPTION_PLANS } from '../../../constants';
import { useConsumption } from '../../../queries';
import { useApp, useAuth } from '../../../hooks';

const UpgradeBanner = () => {
  const { data, isLoading } = useConsumption();
  const { userSubscriptionPlan } = useAuth();
  const { showPlans } = useApp();

  if (isLoading || userSubscriptionPlan === SUBSCRIPTION_PLANS.PRO || data?.credits > 1) {
    return null;
  }

  const isExhausted = data?.credits === 0;
  const shortMessage = isExhausted
    ? 'Daily free credits used up.'
    : 'Only 1 free credit left today.';
  const fullMessage = isExhausted
    ? 'You have exhausted your daily free credit limit.'
    : "You're about to run out of daily free credits (1 left).";
  const statusLabel = isExhausted ? 'Credits used up' : '1 credit left';

  return (
    <button
      type="button"
      onClick={showPlans}
      className="group relative z-20 w-full cursor-pointer overflow-hidden bg-[#5D38DB] text-left text-white transition-colors hover:bg-[#5330c7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60"
      aria-label={`${fullMessage} Upgrade now`}
    >
      <span
        className="pointer-events-none absolute -right-8 -top-10 hidden h-28 w-28 rounded-full bg-white/10 blur-2xl md:block"
        aria-hidden
      />

      {/* Mobile: compact single row */}
      <span className="relative flex items-center gap-2 px-3 py-2 md:hidden">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/15">
          {isExhausted ? (
            <Zap className="h-3.5 w-3.5 text-[#FACC15]" aria-hidden />
          ) : (
            <Sparkles className="h-3.5 w-3.5 text-[#FACC15]" aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12px] leading-tight text-white/95">
          {shortMessage}
        </span>
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-[#FACC15] px-2.5 py-1 text-[11px] font-bold text-[#FACC15]">
          Upgrade
          <ChevronRight className="h-3 w-3" aria-hidden />
        </span>
      </span>

      {/* Desktop: horizontal strip */}
      <span className="relative mx-auto hidden max-w-6xl items-center gap-4 px-6 py-2.5 md:flex lg:px-8">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
          {isExhausted ? (
            <Zap className="h-4 w-4 text-[#FACC15]" aria-hidden />
          ) : (
            <Sparkles className="h-4 w-4 text-[#FACC15]" aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#FACC15]">
            {statusLabel}
          </span>
          <span className="block truncate text-sm text-white/95">{fullMessage}</span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#FACC15] px-4 py-2 text-sm font-bold text-[#3B1D8F] shadow-sm transition-transform group-hover:scale-[1.02] group-active:scale-[0.99]">
          Upgrade Now
          <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      </span>
    </button>
  );
};

export default UpgradeBanner;
